import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import type { PlaygroundLetter } from '@api/demand-playground';
import {
  formatCost,
  formatScore,
  formatSeconds,
  letterBlocks,
  orderedChecks,
} from '../utils/playground';

/** The grader's five scores, in this order, with plain names. */
const GRADER_SCORES: [string, string][] = [
  ['flow', 'Flow'],
  ['formatting', 'Formatting'],
  ['tone', 'Tone'],
  ['support', 'Backed by the records'],
  ['completeness', 'Completeness'],
];

const muted = { color: '#475467' };

function ScoreLine({ letter }: { letter: PlaygroundLetter }) {
  if (letter.score === null || letter.score === undefined) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography fontWeight={600}>
        {`Score ${formatScore(letter.score)} out of 100`}
      </Typography>
      {letter.meets_bar === true ? (
        <Chip size="small" color="success" label="Meets the bar" />
      ) : null}
      {letter.meets_bar === false ? (
        <Chip size="small" color="warning" label="Below the bar" />
      ) : null}
    </Stack>
  );
}

function LetterText({ letter }: { letter: PlaygroundLetter }) {
  return (
    <Stack spacing={1.5} sx={{ '& p': { mt: 0, mb: 1 } }}>
      {(letter.sections ?? []).map((section, i) => {
        const blocks = letterBlocks(section.text);
        return (
          // Section names can repeat; the position is the identity.
          <Box key={`${section.name}-${i}`}>
            <Typography variant="h5" fontWeight={600} sx={{ mb: 0.5 }}>
              {section.name}
            </Typography>
            {blocks.paragraphs.map((p, j) => (
              <Typography
                key={j}
                variant="body2"
                sx={{
                  whiteSpace: 'pre-line',
                  overflowWrap: 'anywhere',
                  mb: 1,
                  lineHeight: 1.6,
                }}
              >
                {p}
              </Typography>
            ))}
          </Box>
        );
      })}
    </Stack>
  );
}

function Results({ letter }: { letter: PlaygroundLetter }) {
  const checks = orderedChecks(letter.checks ?? []);
  // A reused letter's cost and time belong to the earlier run.
  const reused = typeof letter.reused_from === 'number';
  const cost = reused ? '' : formatCost(letter.cost_usd);
  const time = reused ? '' : formatSeconds(letter.seconds);
  return (
    <Stack spacing={2}>
      {letter.judged === false ? (
        <Typography variant="body2" sx={{ color: '#B54708' }}>
          Score from the fixed checks only (the grader did not answer)
        </Typography>
      ) : null}
      {letter.summary ? (
        <Typography variant="body2">{letter.summary}</Typography>
      ) : null}

      {letter.judge_scores ? (
        <Box>
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>
            Reader&apos;s scores
          </Typography>
          <Box
            component="dl"
            sx={{
              display: 'grid',
              gridTemplateColumns: 'auto auto',
              justifyContent: 'start',
              columnGap: 2,
              rowGap: 0.25,
              m: 0,
              '& dd': { m: 0 },
            }}
          >
            {GRADER_SCORES.filter(
              ([key]) => letter.judge_scores?.[key] !== undefined
            ).map(([key, label]) => (
              <Box key={key} sx={{ display: 'contents' }}>
                <Typography component="dt" variant="body2" sx={muted}>
                  {label}
                </Typography>
                <Typography component="dd" variant="body2">
                  {`${letter.judge_scores?.[key]} of 5`}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : null}

      {checks.length ? (
        <Box>
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>
            Checks
          </Typography>
          <Stack component="ul" spacing={0.5} sx={{ pl: 2.5, m: 0 }}>
            {checks.map((check) => (
              <Box component="li" key={check.id} data-testid="check">
                <Typography
                  variant="body2"
                  sx={{ color: check.passed ? '#475467' : '#B42318' }}
                >
                  <strong>{check.passed ? 'Passed' : 'Failed'}:</strong>{' '}
                  {check.label}
                </Typography>
                {!check.passed && check.detail?.length ? (
                  <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
                    {check.detail.map((d, i) => (
                      <Typography component="li" variant="body2" key={i}>
                        {d}
                      </Typography>
                    ))}
                  </Box>
                ) : null}
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}

      <Box>
        <Typography fontWeight={600} sx={{ mb: 0.5 }}>
          {`Wrong facts (${letter.wrong_facts?.length ?? 0})`}
        </Typography>
        {letter.wrong_facts?.length ? (
          <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
            {(letter.wrong_facts ?? []).map((f, i) => (
              <Box component="li" key={i}>
                <Typography variant="body2">{`"${f.quote}"`}</Typography>
                <Typography variant="body2" sx={muted}>
                  {`Records say: ${f.record_says}`}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={muted}>
            None found.
          </Typography>
        )}
      </Box>

      <Box>
        <Typography fontWeight={600} sx={{ mb: 0.5 }}>
          {`Statements the records do not back up (${letter.unsupported?.length ?? 0})`}
        </Typography>
        {letter.unsupported?.length ? (
          <Stack component="ul" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
            {(letter.unsupported ?? []).map((u, i) => (
              <Box component="li" key={i}>
                <Typography variant="body2">{`"${u.quote}"`}</Typography>
                <Typography variant="body2" sx={muted}>
                  {u.why}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={muted}>
            None found.
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={muted}>
        {reused ? (
          <Typography variant="body2">
            {`Reused from run ${letter.reused_from}, no new cost`}
          </Typography>
        ) : null}
        {cost ? (
          <Typography variant="body2">{`Cost: ${cost}`}</Typography>
        ) : null}
        {time ? (
          <Typography variant="body2">{`Time: ${time}`}</Typography>
        ) : null}
        {typeof letter.words === 'number' ? (
          <Typography variant="body2">
            {`Words: ${letter.words.toLocaleString('en-US')}`}
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

interface Props {
  title: string;
  letter: PlaygroundLetter | null;
  /** The whole run is still going, so a missing letter is not written yet. */
  runInProgress: boolean;
  testId: string;
}

/**
 * One side of the comparison (ZLAYT-1660): a short score line on top, the
 * letter, then under it the checks, wrong facts, unsupported statements,
 * cost, time and words.
 */
function LetterColumn({ title, letter, runInProgress, testId }: Props) {
  let body;
  // Anything not done or failed is still being worked on; such a letter
  // carries no sections, checks or scores yet (backend contract).
  if (!letter || (letter.status !== 'done' && letter.status !== 'failed')) {
    let doing = 'Waiting to start';
    if (letter) {
      doing =
        letter.phase === 'scoring'
          ? 'Checking this letter'
          : 'Writing this letter';
    }
    // A letter still running is in progress whatever the run says.
    body =
      letter || runInProgress ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={16} />
          <Typography variant="body2" sx={muted}>
            {doing}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body2" sx={muted}>
          No letter was written.
        </Typography>
      );
  } else if (letter.status === 'failed') {
    body = (
      <Alert severity="error">
        {letter.error || 'This letter could not be written.'}
      </Alert>
    );
  } else {
    body = (
      <Stack spacing={2}>
        <ScoreLine letter={letter} />
        <LetterText letter={letter} />
        <Divider />
        {/* No "None found" before the check has run: it reads as a clean letter. */}
        {letter.score !== null && letter.score !== undefined ? (
          <Results letter={letter} />
        ) : null}
        {(letter.score === null || letter.score === undefined) &&
        runInProgress ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="body2" sx={muted}>
              Checking this letter
            </Typography>
          </Stack>
        ) : null}
        {(letter.score === null || letter.score === undefined) &&
        !runInProgress ? (
          <Typography variant="body2" sx={muted}>
            This letter was not checked.
          </Typography>
        ) : null}
      </Stack>
    );
  }

  return (
    <Box
      data-testid={testId}
      sx={{
        border: '1px solid #EAECF0',
        borderRadius: 1,
        p: 2,
        minWidth: 0,
      }}
    >
      <Typography variant="h4" fontWeight={500} sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      {body}
    </Box>
  );
}

export default LetterColumn;
